import { buttonClasses } from '@mui/material/Button'
import { Theme, alpha } from '@mui/material/styles'

import {
  categoricalTokens,
  colorTokens,
  icons,
  layout,
  semanticTokens,
  typography as typeTokens,
} from '../tokens.generated'

// ----------------------------------------------------------------------
// Flat, border-first buttons: one solid-accent primary, surface + hairline
// secondary, ghost text, and `ink` for the vehicle commands that open a car or
// make it drivable. No resting shadows; hover changes colour and nothing else.
//
// A ghost or secondary button is a surface, so it takes `theme.hover.surface`,
// the one value a row, a nav item and a menu item take. A filled button is
// not: moving a solid fill onto the sunken surface would throw away the ink it
// promised, so each filled variant darkens its own fill by a step instead.
//
// Geometry is the control contract (handover 4.1): three fixed heights, and the
// only vertical padding a control takes is the UI face's cap rise, which sets
// its word on its capitals (`controlSize`). Height plus centred flex alignment
// puts the label where it belongs; a height and a block padding that disagree
// is where most of the old misalignment came from. The height is the same with
// or without an icon, so a row of buttons never steps.
//
// `beastPrimary` keeps its name and its callers and is drawn as a secondary.
// It is the variant `HelpButton` draws, on the import, incomplete-list and
// Stripe screens, and the least important control on every one of them. It
// was five hand-rolled copies across four files before support became one
// component and one address.
// ----------------------------------------------------------------------

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    /** Consequence class 2 and 3 vehicle commands. The ink fill, inverted per scheme. */
    ink: true
    /** `HelpButton`'s. A secondary, despite the name. */
    beastPrimary: true
    /**
     * An action with a physical cost to the car rather than to a record:
     * `Wake & refresh` drains the 12V battery to poll a sleeping vehicle.
     * Outlined in the `attention` semantic (the system's orange) because
     * the warning yellow has no slot that clears 3:1 on white.
     */
    caution: true
    /**
     * A commit that sets aside something the console raised, rather than
     * acting on it: `Dismiss hint` on the Costs page. Filled in the attention
     * semantic, the tone the hint was raised in, with `onAttention` ink.
     */
    setAside: true
    /**
     * The door to setting something aside: `Dismiss` on a hint card, which
     * asks why before anything is dismissed. The attention tint with its own
     * ink at rest, a step deeper under the pointer, and no edge.
     */
    dismiss: true
    /**
     * A list's own output: `Export all` and `Export filtered`. The accent's
     * pastel with its ink, no edge, a step brighter under the pointer.
     */
    soft: true
    /**
     * A control that changes what a list shows rather than doing anything
     * to it: `Filters`. The neutral pastel with the primary ink, no edge, a
     * step along the neutrals under the pointer.
     */
    quiet: true
  }
}

/**
 * Every outlined colour keeps its own hue under the pointer.
 *
 * MUI composes the general `outlined` slot before `outlined<Colour>`, so the
 * hairline hover there reached every coloured outlined button and greyed an
 * edge that was carrying meaning. Written as a loop rather than five copies,
 * so a colour used for the first time next year is covered without a sixth.
 *
 * A `variants` entry is composed after `styleOverrides`, which is what makes
 * this the last word. The per-slot restatement in `outlinedError` stays: it
 * states the *resting* value, and this states that the hover does not move it.
 */
const OUTLINED_COLOURS = [
  'primary',
  'info',
  'success',
  'warning',
  'error',
] as const

/**
 * The one focus treatment for every control: the accent ring, at the width the
 * system publishes, drawn as a shadow so it sits outside the control's own
 * border and never changes its size.
 */
export const focusRingShadow = (theme: Theme) =>
  `0 0 0 ${layout.focusRingWidth}px ${theme.palette.accentRing}`

/**
 * Both spellings of keyboard focus. The browser's `:focus-visible` and MUI's
 * polyfilled `.Mui-focusVisible` class, so the ring survives a `disableElevation`
 * rule that clears the shadow on the class alone.
 */
export const FOCUS_VISIBLE = '&:focus-visible, &.Mui-focusVisible'

/**
 * The edge a ghost reserves at rest, in nothing, so its hover has somewhere to
 * go (see `text` below). Its label stands this far plus the control's inset
 * inside its box, which is what a table cell pulls it out by.
 */
export const GHOST_EDGE = 1

/**
 * How much of the attention fill a `dismiss` button's ground is under the
 * pointer in dark, where its resting tint is the same fill at 18 percent.
 */
const DISMISS_HOVER_ALPHA = 0.32

/**
 * How much of the accent fill a `soft` button's ground is under the pointer,
 * so it brightens: at rest it is the accent's pastel, the same fill at about a
 * fifth, and brighter is less of it on a light page and more of it on a dark
 * one.
 */
const SOFT_HOVER_ALPHA = { light: 0.08, dark: 0.32 } as const

/**
 * The only movement a control is allowed: a half pixel of press. Hover never
 * moves anything. There is no token for a press offset yet; this is the single
 * place the value is written.
 */
const PRESS_OFFSET = 'translateY(0.5px)'

/** One control size, entirely from the layout, icon and type tokens. */
const controlSize = (
  theme: Theme,
  size: {
    height: number
    inset: number
    gap: number
    fontSize: number
  },
) => {
  // The label centred on its capitals rather than on its line. Heebo's
  // capitals sit `capRise` of an em above the line's middle, so a word centred
  // by its line read 0.6px high in a 40px button (asked on 2026-10-31: "is
  // the word Export actually middle in relation to the btn? ... Should be (for
  // all words inside btns)"). The content box drops by the rise, so the word
  // does, and the glyph slots climb back by it, so a glyph stays on the
  // button's middle. In pixels per size: an em on the slot would be the
  // glyph's own.
  const rise = typeTokens.families.ui.capRise * size.fontSize
  return {
    height: size.height,
    paddingInline: size.inset,
    paddingTop: 2 * rise,
    paddingBottom: 0,
    gap: size.gap,
    fontSize: theme.typography.pxToRem(size.fontSize),
    [`& .${buttonClasses.startIcon}, & .${buttonClasses.endIcon}`]: {
      marginTop: -2 * rise,
    },
  }
}

/**
 * The glyph a button slot renders when its icon scales with `fontSize`. Iconify
 * sizes itself by width, so this only reaches icons that inherit their size.
 */
const iconSlotSize = (theme: Theme, size: number) => ({
  '& > *:nth-of-type(1)': {
    fontSize: theme.typography.pxToRem(size),
  },
})

export default function Button(theme: Theme) {
  // The hover fills, resolved for the scheme this theme is being built for.
  const semantics = semanticTokens[theme.palette.mode]
  const colors = colorTokens[theme.palette.mode]

  return {
    MuiButton: {
      defaultProps: {
        color: 'secondary',
        disableElevation: true,
      },
      variants: [
        {
          // Named `beastPrimary` and drawn for `Need help?`, which is the
          // least important thing on any screen it appears on. It drew a
          // 999px white
          // pill with a drop shadow: on the Stripe page it was wider, brighter
          // and more raised than the actual primary beside it, and in light
          // mode it sat on a white surface with no border, defined only by
          // that shadow.
          //
          // A pill is for a thing that shows state, never for a thing you
          // press, and no button in this product carries a shadow. So it is
          // the secondary it always was: control radius, surface fill, a
          // hairline that holds it on white.
          props: { variant: 'beastPrimary' },
          style: {
            'color': theme.palette.text.primary,
            'backgroundColor': theme.palette.background.paper,
            'borderRadius': theme.radii.control,
            'border': `1px solid ${theme.palette.grey[400]}`,
            'boxShadow': 'none',
            '&:hover': {
              backgroundColor: theme.hover.surface,
              boxShadow: 'none',
            },
            // Stated, because MUI only rewrites a disabled border for its own
            // `outlined` variant. Without this the edge and the fill stayed at
            // full strength with only the label greyed.
            '&.Mui-disabled': {
              color: theme.palette.action.disabled,
              borderColor: theme.palette.action.disabledBackground,
              backgroundColor: 'transparent',
            },
          },
        },
        {
          // `grey[900]` is the neutral ramp's ink, so the fill inverts with the
          // scheme and `onInk` is the ink that reads on it in both. Hover moves
          // one step down the ramp and lifts nothing.
          props: { variant: 'ink' },
          style: {
            'color': colors.onInk,
            'backgroundColor': theme.palette.grey[900],
            'border': 'none',
            '&:hover': {
              backgroundColor: theme.palette.grey[800],
            },
            '&.Mui-disabled': {
              color: theme.palette.action.disabled,
              backgroundColor: theme.palette.action.disabledBackground,
            },
          },
        },
        {
          // **Caution: a physical cost, not an urgent one.**
          //
          // `Wake & refresh` on the maintenance tab said `color="warning"`
          // already; what it painted was the defect. The theme's `outlined`
          // slot overwrote MUI's coloured edge with `grey[400]`, and the
          // label inherited `warning.main`, which is the meter yellow:
          // 1.51:1 on a white card, against a 4.5 requirement. The button was
          // legible only in dark mode, which is why it shipped.
          //
          // Yellow has no slot that can carry this: `warning.fill` is 1.51:1
          // and `warning.deep` 2.50:1 on white, so a yellow-edged button has
          // no boundary either. `attention` is the system's orange and its
          // slots are authored for exactly this: the same pairing, and for
          // the same reason, as the needs-attention outcome mark, which takes
          // `deep` because the fill is 2.3:1 on white.
          //
          // A variant and not a palette colour: adding `palette.attention`
          // with a `main` and a `light` would make MUI's built-in per-colour
          // Alert styles start matching the `attention` severity that
          // `overrides/Alert.tsx` supplies in full, which is a regression in
          // five tones and two schemes in exchange for nothing. And it is
          // named for the consequence rather than for the colour, because
          // that is how this system chooses a variant.
          props: { variant: 'caution' },
          style: {
            'color': semantics.attention.text,
            'backgroundColor': 'transparent',
            'borderStyle': 'solid',
            'borderWidth': 1,
            'borderColor': semantics.attention.deep,
            'borderRadius': theme.radii.control,
            'boxShadow': 'none',
            '&:hover': {
              backgroundColor: semantics.attention.soft,
              borderColor: semantics.attention.deep,
            },
            '&.Mui-disabled': {
              color: theme.palette.action.disabled,
              borderColor: theme.palette.action.disabledBackground,
              backgroundColor: 'transparent',
            },
          },
        },
        {
          // **Set aside: dismissing something the console raised.**
          //
          // The Dismiss hint dialog commits in the attention fill, the tone
          // the hint itself was raised in, decided on the Costs designs. The
          // ink is `onAttention`, near black in both schemes: white is 2.3:1
          // on the orange. Hover moves to `fillHover`, which the ink still
          // clears. A variant rather than a palette colour for the reason
          // `caution` gives: a `palette.attention` would start MUI's own
          // per-colour Alert styles matching the `attention` severity.
          props: { variant: 'setAside' },
          style: {
            'color': colors.onAttention,
            'backgroundColor': semantics.attention.fill,
            'border': 'none',
            'boxShadow': 'none',
            '&:hover': {
              backgroundColor: semantics.attention.fillHover,
            },
            '&.Mui-disabled': {
              color: theme.palette.action.disabled,
              backgroundColor: theme.palette.action.disabledBackground,
            },
          },
        },
        {
          // **Dismiss: the door to the set-aside commit.**
          //
          // On a hint card it was a ghost: nothing at rest, and an edge and a
          // grey fill under the pointer, beside an outlined action, so the
          // card's two buttons were two boxes the moment either was touched
          // (asked for on 2026-10-11: "no border line for the Dismiss btn,
          // but do use an orangish color combo ... that shows already in
          // non-hover state and just gets darker on hover").
          //
          // It is the attention tint, the pair a `Needs attention` chip wears,
          // so it reads as the quiet half of the orange commit it opens
          // (`setAside`) and never outranks the card's own action. Under the
          // pointer the ground deepens and the ink stays: in light to the
          // bright fill, which the tint's ink clears at 5.9:1; in dark the
          // tint is the fill at 18 percent, and it goes to a third.
          props: { variant: 'dismiss' },
          style: {
            'color': semantics.attention.text,
            'backgroundColor': semantics.attention.soft,
            'border': 'none',
            'boxShadow': 'none',
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'light'
                  ? semantics.attention.brightFill
                  : alpha(semantics.attention.fill, DISMISS_HOVER_ALPHA),
            },
            '&.Mui-disabled': {
              color: theme.palette.action.disabled,
              backgroundColor: theme.palette.action.disabledBackground,
            },
          },
        },
        {
          // **Soft: a list's own output.**
          //
          // Export all and Export filtered were outlined, the hairline
          // secondary every button in the console is, so the two buttons over
          // a list read as two boxes with nothing to say which was which
          // (asked for on 2026-11-02: no border, pastel blue for Export and a
          // grey ground for Filters). The accent's pastel, the pair the
          // categorical blue tag and an info hint wear, so it reads as the
          // console's colour without the weight of the solid primary. The
          // edge is kept and transparent, so the box is the outlined one's to
          // the pixel and a row of the two never steps. Under the pointer it
          // only brightens (asked for the same day).
          props: { variant: 'soft' },
          style: {
            'color': semantics.info.text,
            'backgroundColor': semantics.info.soft,
            'borderStyle': 'solid',
            'borderWidth': GHOST_EDGE,
            'borderColor': 'transparent',
            'boxShadow': 'none',
            '&:hover': {
              backgroundColor: alpha(
                semantics.info.fill,
                SOFT_HOVER_ALPHA[theme.palette.mode],
              ),
            },
            '&.Mui-disabled': {
              color: theme.palette.action.disabled,
              backgroundColor: theme.palette.action.disabledBackground,
            },
          },
        },
        {
          // **Quiet: a control over what a list shows.**
          //
          // Filters, beside the exports. The neutral pastel, the grey a
          // categorical tag wears, with the primary ink: the ink is what says
          // it can be pressed, since a disabled button is a grey fill too but
          // in the disabled ink. Under the pointer the ground moves to
          // `borderStrong` and nothing else moves, on the page and on a table's
          // head band alike. That reads brighter in dark and darker in light:
          // a brighter button in light is the page's own colour, and at the
          // card's white it merged with the page and disappeared under the
          // pointer (asked on 2026-11-02 for that one to go darker instead).
          props: { variant: 'quiet' },
          style: {
            'color': theme.palette.text.primary,
            'backgroundColor': categoricalTokens[theme.palette.mode].grey.bg,
            'borderStyle': 'solid',
            'borderWidth': GHOST_EDGE,
            'borderColor': 'transparent',
            'boxShadow': 'none',
            '&:hover': {
              backgroundColor: colors.borderStrong,
            },
            '&.Mui-disabled': {
              color: theme.palette.action.disabled,
              backgroundColor: theme.palette.action.disabledBackground,
            },
          },
        },
        ...OUTLINED_COLOURS.map((color) => ({
          props: { variant: 'outlined' as const, color },
          style: {
            '&:hover': { borderColor: theme.palette[color].main },
          },
        })),
      ],
      styleOverrides: {
        root: {
          'display': 'inline-flex',
          'alignItems': 'center',
          'justifyContent': 'center',
          'lineHeight': 1,
          'paddingBlock': 0,
          // A label is one line by contract. A wrapped label inside a fixed
          // height would spill out of the box rather than grow it.
          'whiteSpace': 'nowrap',
          'borderRadius': theme.radii.control,
          'fontWeight': typeTokens.weights.semibold,
          'textTransform': 'none',
          'fontVariantNumeric': typeTokens.numeric.fontVariant,
          'boxShadow': 'none',
          'transition': theme.motion.state.transition(
            'background-color',
            'border-color',
            'color',
            'box-shadow',
          ),
          '&:hover': {
            boxShadow: 'none',
          },
          [FOCUS_VISIBLE]: {
            outline: 'none',
            boxShadow: focusRingShadow(theme),
          },
          '&:active': {
            transform: PRESS_OFFSET,
          },
          '.MuiTypography-root': {
            textTransform: 'initial',
          },
        },
        sizeSmall: {
          ...controlSize(theme, {
            height: layout.controlHeightSmall,
            inset: layout.controlInsetSmall,
            gap: layout.controlGapSmall,
            fontSize: typeTokens.console.small.size,
          }),
          // A brand mark (Google's G, Apple's apple) is painted to the edge of
          // its box, where every line glyph stands a stroke or two inside its
          // own, so at the small gap it touched its label: `Unlink Google`
          // read as one shape. It takes the regular gap instead. The larger
          // buttons already have it.
          [`& .${buttonClasses.startIcon} > [data-brand-mark]`]: {
            marginRight: layout.controlGap - layout.controlGapSmall,
          },
        },
        sizeMedium: controlSize(theme, {
          height: layout.controlHeight,
          inset: layout.controlInset,
          gap: layout.controlGap,
          fontSize: typeTokens.console.body.size,
        }),
        sizeLarge: controlSize(theme, {
          height: layout.controlHeightLarge,
          inset: layout.controlInsetLarge,
          gap: layout.controlGap,
          fontSize: typeTokens.console.groupTitle.size,
        }),
        // `gap` owns the space between glyph and label now. The margins MUI
        // puts on the icon slots are why icon buttons had uneven left and
        // right insets.
        startIcon: {
          marginLeft: 0,
          marginRight: 0,
        },
        endIcon: {
          marginLeft: 0,
          marginRight: 0,
        },
        iconSizeSmall: iconSlotSize(theme, icons.sizes.dense),
        iconSizeMedium: iconSlotSize(theme, icons.sizes.control),
        iconSizeLarge: iconSlotSize(theme, icons.sizes.toolbar),
        // contained: solid fill, flat, darken on hover
        containedInherit: {
          'color': theme.palette.grey[800],
          'backgroundColor': theme.palette.grey[200],
          '&:hover': {
            backgroundColor: theme.palette.grey[300],
          },
        },
        // Hover moves the fill away from its own ink, so the ink a fill states
        // stays legible while a pointer is over it. These used to hover to the
        // MUI `dark` slot, which for three of the five semantics is a text
        // colour rather than a fill: a solid Approve button fell to 2.9:1 and
        // the primary call to action to 3.4:1 in dark mode.
        containedPrimary: {
          '&:hover': { backgroundColor: semantics.info.fillHover },
        },
        containedSecondary: {
          '&:hover': { backgroundColor: semantics.info.fillHover },
        },
        containedInfo: {
          '&:hover': { backgroundColor: semantics.info.fillHover },
        },
        containedSuccess: {
          '&:hover': { backgroundColor: semantics.success.fillHover },
        },
        containedWarning: {
          '&:hover': { backgroundColor: semantics.warning.fillHover },
        },
        containedError: {
          '&:hover': { backgroundColor: semantics.error.fillHover },
        },
        // outlined: surface + hairline border (the secondary action)
        outlined: {
          // A secondary is a surface, so it answers like one. MUI's own
          // outlined hover is a wash of the button's colour, which put an
          // accent tint under every default button in the console and made the
          // dismiss beside a primary read as a second accent control.
          'borderColor': theme.palette.grey[400],
          '&:hover': {
            backgroundColor: theme.hover.surface,
            // The edge too, and not for decoration: a secondary standing on a
            // panel head is already painted the colour the fill moves it to,
            // so the fill on its own answered nothing a person could see.
            borderColor: theme.hover.edge,
          },
        },
        outlinedInherit: {
          'border': `1px solid ${theme.palette.grey[400]}`,
          '&:hover': {
            backgroundColor: theme.hover.surface,
            borderColor: theme.hover.edge,
          },
        },
        // `danger` is outlined error. The hairline rule above would otherwise
        // turn its border grey, and MUI's label is the raw error fill, which
        // is a meter colour and does not clear 4.5:1 on a surface. The label
        // takes the error text role, the border the error fill.
        outlinedError: {
          // The one outlined button that is not a surface: a destructive
          // action says so under the pointer, on its own tint.
          'color': semantics.error.text,
          'borderColor': theme.palette.error.main,
          '&:hover': {
            backgroundColor: semantics.error.soft,
            // **Restated, and this is the whole of it.** MUI composes
            // `outlined` before `outlinedError`, so the general hairline
            // hover above landed on the destructive control too and turned
            // `Remove connection`'s red edge grey the moment a pointer
            // touched it. A control that has an edge keeps its hue; the fill
            // is what answers. The grey secondary above is the exception and
            // says why: it has no hue to keep, and its fill alone answers
            // nothing on a panel head.
            borderColor: theme.palette.error.main,
          },
        },
        // text / ghost
        text: {
          // The edge is reserved at rest, in nothing, so the hover has
          // somewhere to go. A ghost standing on a sunken panel (the
          // `Show` beside `Developer`, every `SectionPanel` action) is
          // already painted the colour the fill hovers to, so measured with a
          // real pointer its rest and hover paint came back byte-identical:
          // transparent over `background.neutral` on one side,
          // `theme.hover.surface` over the same fill on the other. The edge is
          // the half of the answer that does not depend on what is behind it,
          // which is what `theme.hover.edge` exists for.
          //
          // Written as longhands and declared at rest rather than added on
          // hover: a border that appears under the pointer grows the box by
          // two pixels and shifts every neighbour on the row. This way the
          // hover moves paint and only paint.
          'borderStyle': 'solid',
          'borderWidth': GHOST_EDGE,
          'borderColor': 'transparent',
          '&:hover': {
            backgroundColor: theme.hover.surface,
            borderColor: theme.hover.edge,
          },
        },
        // The ink step as well. It is a no-op where the ghost already inherits
        // the primary ink (which is most of them, and is the second half of
        // the same silence), but it is what answers for a ghost drawn in the
        // secondary ink, so both halves are stated and the edge above carries
        // the ones the ink cannot.
        textInherit: {
          '&:hover': {
            backgroundColor: theme.hover.surface,
            color: theme.hover.ink,
          },
        },
      },
    },
  }
}
